<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import {Dialog, DialogPanel, DialogTitle, TransitionRoot} from '@headlessui/vue'
import DynamicForm from '@/views/DynamicForm.vue';
import {useRoute} from 'vue-router';
import clientService from '@/services/ClientService';
import {useI18n} from 'vue-i18n';
import DocumentService from "@/services/DocumentService";
import {showSuccessMessage} from "@/helper";

const props = defineProps({
  meetingRecord: {type: Object, required: false},
  disabled: {type: Boolean, required: false, default: false},
  prefillObject: {type: Object, required: false, default: null},
});

const {t} = useI18n()
const params = useRoute().params;
const selectedForm = ref(null);
const dynamicFormRef = ref(null);
const form = ref({...props.meetingRecord});
const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const availableMeetingRecords = ref(null);
const availableContracts = ref([]);
const selectedContractId = ref(null);
const documentIsGenerating = ref(false);
const formKey = ref(0);
const isInitialized = ref(false);

// Pro nové záznamy: zobrazit select jen když šablona obsahuje ${CLIENT_CONTRACT_NUMBER}.
// Pro editaci existujících: zobrazit vždy, pokud má klient nějaké smlouvy — uživatel
// může změnit přiřazenou smlouvu, která se použije při generování dokumentu.
const showContractSelect = computed(() =>
  !props.meetingRecord?.id && (
    selectedForm.value?.has_contract_number_keyword === true ||
    availableContracts.value.length > 0
  )
);

const closeModalWith = (value, state) => {
  form.value.data = dynamicFormRef.value?.config;
  // Uložit vybranou smlouvu do _meta — používá se při přegenerování dokumentu
  if (selectedContractId.value) {
    form.value.data = { ...(form.value.data ?? {}), _meta: { contract_id: selectedContractId.value } };
  }
  form.value.state = state;
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      if (!state) {
        if (selectedForm.value.confirmation_required) {
          form.value.state = 'WAITING';
        } else {
          form.value.state = 'TO_REVIEW';
        }
      }
      emit('confirm', form.value);
    } else {
      emit('cancel');
    }
  }, 300);
}

function openModal() {
  isOpen.value = true
}

onMounted(() => {
  clientService.getAllActiveTemplates(params.id, 'MEETING_RECORD')
      .then(({ templates, contracts }) => {
        availableMeetingRecords.value = templates?.map(form => ({
          ...form.template,
          id: form.id,
          has_contract_number_keyword: form.has_contract_number_keyword ?? false,
        }));
        availableContracts.value = contracts ?? [];
        // Předvybrání první smlouvy jako výchozí hodnoty
        selectedContractId.value = contracts?.[0]?.id ?? null;

        if (props.meetingRecord?.data) {
          selectedForm.value = props.meetingRecord.data;
          // Obnovit uloženou smlouvu z _meta při editaci existujícího záznamu
          selectedContractId.value = props.meetingRecord.data?._meta?.contract_id ?? selectedContractId.value;
        } else {
          selectedForm.value = null;
        }
        nextTick(() => { isInitialized.value = true; });
      })
})

// Při změně smlouvy znovu načte všechny šablony s novým contract_id, aby se
function mergeAnswersIntoTemplate(freshTemplate, currentForm) {
  if (!currentForm?.content || !freshTemplate?.content) return freshTemplate;
  const currentValues = {};
  for (const page of currentForm.content) {
    for (const question of page.questions ?? []) {
      if (question.id != null) currentValues[question.id] = question.value;
    }
  }
  return {
    ...freshTemplate,
    content: freshTemplate.content.map(page => ({
      ...page,
      questions: (page.questions ?? []).map(q => ({
        ...q,
        value: q.id in currentValues ? currentValues[q.id] : q.value,
      })),
    })),
  };
}

watch(selectedContractId, (newContractId) => {
  if (!selectedForm.value || !isInitialized.value) return;
  const currentId = selectedForm.value.id;
  const currentForm = selectedForm.value;
  clientService.getAllActiveTemplates(params.id, 'MEETING_RECORD', newContractId)
    .then(({ templates }) => {
      availableMeetingRecords.value = templates?.map(t => ({
        ...t.template,
        id: t.id,
        has_contract_number_keyword: t.has_contract_number_keyword ?? false,
      }));
      const fresh = availableMeetingRecords.value?.find(t => t.id === currentId) ?? null;
      selectedForm.value = fresh ? mergeAnswersIntoTemplate(fresh, currentForm) : null;
      formKey.value++;
    });
});

function generateDocument() {
  documentIsGenerating.value = true;
  const body = {
    model_id: props.meetingRecord?.id,
    model_type: '\\App\\Models\\MeetingRecord',
  }
  DocumentService.generateDocument(body).then(response => {
    documentIsGenerating.value = false;
    showSuccessMessage(t('general.document_generated_successfully'));
    emitter.emit('documentGenerated');
  })
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle as="h3" class="modal-title">
                {{ t('createEditMeetingRecordModal.meeting_records') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <form :disabled="props.disabled" @submit.prevent="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div v-if="!form.id" class="form-group">
                    <label for="formTypeSelect" class="form-label">{{ t('createEditMeetingRecordModal.choose_form') }}</label>
                    <select id="formTypeSelect" v-model="selectedForm" class="form-select">
                      <option v-for="form of availableMeetingRecords" :value="form">{{form.name}}</option>
                    </select>
                  </div>
                </div>
                <div v-if="showContractSelect" class="row mb-3">
                  <div class="form-group">
                    <label for="contractSelect" class="form-label">{{ t('general.contract') }}</label>
                    <select id="contractSelect" v-model="selectedContractId" class="form-select">
                      <option v-for="contract of availableContracts" :key="contract.id" :value="contract.id">
                        {{ contract.contract_number }}
                      </option>
                    </select>
                  </div>
                </div>
                <div v-if="availableMeetingRecords?.length === 0 && !props.disabled" class="alert alert-danger"> {{ t('createEditMeetingRecordModal.no_forms_available') }} </div>
                <DynamicForm :key="(selectedForm?.id ?? 0) + '-' + formKey" v-if="selectedForm" :disabled="props.disabled" :config="selectedForm" ref="dynamicFormRef" :prefill-object="props.prefillObject"></DynamicForm>
              </div>

              <div class="modal-footer">
                <button v-if="!meetingRecord?.id && !props.disabled" class="btn btn-link text-decoration-underline text-dark" type="button" @click="closeModalWith('cancel')">{{ t('createEditMeetingRecordModal.cancel') }}</button>
                <button v-if="!props.disabled && availableMeetingRecords?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="button" @click="closeModalWith('confirm', 'CONCEPT')">{{ t('createEditMeetingRecordModal.save_as_concept') }}</button>
                <button v-if="!props.disabled && availableMeetingRecords?.length > 0" :disabled="!selectedForm" class="btn btn-primary btn-md" type="submit">{{ t('createEditMeetingRecordModal.save_and_send_to_client') }}</button>
                <button v-if="meetingRecord?.id" @click="generateDocument" class="btn btn-primary btn-md" type="button" id="generate-document-button"><span v-if="!documentIsGenerating">{{ t('general.generate_document') }}</span><span v-else>{{ t('general.generating') }}</span></button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

